// sim/upgradePacing.ts — UPGRADE-PACING LAYER: a pure, deterministic policy that decides
// HOW BIG each player box reward is relative to what they already hold, so a long run of
// opens hits a CONTROLLED upgrade-magnitude distribution instead of a random walk.
//
// THE PROBLEM. The relative-loot layer (geneLootScaling.ts) makes a reward's POWER track the
// player's baseline by RARITY — Common is a sidegrade, Legendary a solid step. But WHICH rarity
// a reward rolls was still left to the forge's raw `RARITY_WEIGHT` table, so the *magnitude*
// distribution of player upgrades was an accident of that table, not a designed pace. This layer
// owns that decision: at the reference box tier it forces the per-reward rarity to a designed
// distribution ≈ 40% sidegrades / 35% small / 15% moderate / 7% major / 3% exceptional.
//
// THE MODEL. Five upgrade TIERS, each pinned to a rarity so rarity↔power↔traits stay COHERENT
// (a "major" reward really is a Legendary with Legendary traits, not a Common forged big):
//
//     sidegrade ↔ Common · small ↔ Uncommon/Rare · moderate ↔ Epic · major ↔ Legendary ·
//     exceptional ↔ Apex
//
// The inventory rarity column maxes at Legendary (RARITY.Legendary == 4); "Apex" is the synthetic
// top band index 5 of REWARD_RATIO_BAND, NOT a storable rarity. So an `exceptional` reward is a
// Legendary GENE (max storable rarity + Legendary traits) fitted to the bigger Apex POWER window —
// a big-but-bounded step that never invents a rarity the inventory cannot hold. Every other tier's
// band index equals its rarity. The Apex window caps at ≤1.85× baseline, so even the exceptional
// reward is bounded (it can't invalidate a build), and because every band is a multiple of the
// player's CURRENT baseline, rewards track the player and never inflate long-term.
//
// PURE + DETERMINISTIC: integer weight tables, a single cumulative-walk pick off an integer roll,
// no RNG ownership, no sim mutation, no DOM. The forge (web/evolutionBox.ts) supplies the roll
// from its per-open RNG stream and applies the chosen rarity/band; this module only decides. So the
// live open and a save-replay pick the identical tier. Headlessly tested (upgradePacing.test.mjs).

import { RARITY } from '../types.ts';
import { APEX_TIER } from './geneLootScaling.ts';

/** The five upgrade magnitudes, ascending. Index is the canonical tier order. */
export type UpgradeTier = 'sidegrade' | 'small' | 'moderate' | 'major' | 'exceptional';

/** Canonical ascending tier order (also the strict power ordering they must respect). */
export const UPGRADE_TIERS: readonly UpgradeTier[] = [
  'sidegrade', 'small', 'moderate', 'major', 'exceptional',
];

/**
 * A weighted rarity SLOT in the pacing table. `tier` is the upgrade magnitude it contributes to;
 * `rarity` is the INVENTORY rarity the reward is set to (RARITY.*, ≤ Legendary — what drives the
 * gene's traits, expression, duplicate detection and display); `band` is the REWARD_RATIO_BAND
 * index that selects the reward's POWER window (== rarity for every tier except `exceptional`,
 * which uses the synthetic Apex band so the gene stays a storable Legendary but reaches the bigger
 * Apex window). Multiple slots can share a tier (the `small` tier is split Uncommon+Rare), which is
 * why the table is a list of slots rather than one row per tier.
 */
export interface PacingSlot {
  readonly tier: UpgradeTier;
  readonly rarity: number;
  readonly band: number;
}

/**
 * The pacing slots, in fixed order (this order is the deterministic cumulative-walk order).
 * `small` is intentionally two slots (Uncommon, Rare) so its 35% splits across both rarities,
 * matching the spec ("split small across Uncommon+Rare"). Every other tier is a single slot.
 *
 *   sidegrade  → Common    (band Common)
 *   small      → Uncommon  (band Uncommon)  +  Rare (band Rare)
 *   moderate   → Epic      (band Epic)
 *   major      → Legendary (band Legendary)
 *   exceptional→ Legendary (band APEX_TIER) — storable Legendary, Apex power window
 */
export const PACING_SLOTS: readonly PacingSlot[] = [
  { tier: 'sidegrade',   rarity: RARITY.Common,    band: RARITY.Common },
  { tier: 'small',       rarity: RARITY.Uncommon,  band: RARITY.Uncommon },
  { tier: 'small',       rarity: RARITY.Rare,      band: RARITY.Rare },
  { tier: 'moderate',    rarity: RARITY.Epic,      band: RARITY.Epic },
  { tier: 'major',       rarity: RARITY.Legendary, band: RARITY.Legendary },
  { tier: 'exceptional', rarity: RARITY.Legendary, band: APEX_TIER },
];

/**
 * REFERENCE weights (the base box tier), summing to exactly 100, that yield the spec
 * distribution ≈ 40 / 35 / 15 / 7 / 3 (sidegrade / small / moderate / major / exceptional).
 * Index-aligned with PACING_SLOTS. The `small` tier's 35 is split 20 (Uncommon) + 15 (Rare).
 */
export const REFERENCE_WEIGHTS: readonly number[] = [
  40, // sidegrade   (Common)
  20, // small       (Uncommon)
  15, // small       (Rare)
  15, // moderate    (Epic)
  7,  // major       (Legendary)
  3,  // exceptional (Apex window)
];

/**
 * Per-box-tier UPWARD LIFT applied to the reference weights, expressed as integer point-shifts
 * MOVED from the sidegrade slot up the table. Each entry is how many weight points the box tier
 * pulls OUT of `sidegrade` (slot 0) and redistributes into the higher tiers, so advancing boxes
 * feel strictly better (fewer sidegrades, more moderate/major/exceptional) while the WEIGHTS STILL
 * SUM TO 100. Index = box tier (0 = the reference tier ⇒ no shift; matches the spec target).
 *
 * The redistribution split is fixed (`LIFT_SPLIT` below). Higher box tiers simply shift more.
 */
export const BOX_TIER_LIFT: readonly number[] = [0, 6, 14, 24, 36];

/**
 * How a lifted point-budget is distributed UP the table (the points pulled out of `sidegrade`).
 * Index-aligned with PACING_SLOTS slots 1..5 (sidegrade slot 0 is the SOURCE, never a sink). The
 * fractions sum to 1.0; the integer split rounds deterministically and the remainder lands on the
 * top (exceptional) slot, so the row always re-sums to 100. Weighted toward the middle tiers so a
 * box-tier lift feels like "more solid upgrades" first, "more jackpots" second.
 */
const LIFT_SPLIT: readonly number[] = [0.25, 0.25, 0.25, 0.15, 0.10];

/**
 * The pacing weights for a given BOX TIER: the reference weights with the box tier's lift moved
 * from sidegrade up into the higher tiers. Pure; always returns a fresh array summing to 100 with
 * every entry ≥ 0 (the lift is clamped so it can never drive sidegrade negative). `boxTier` out of
 * range clamps to the table ends.
 *
 * Determinism: integer arithmetic with a fixed remainder rule, so the same `boxTier` always yields
 * the identical weight row.
 */
export function boxTierWeights(boxTier: number): number[] {
  const w = REFERENCE_WEIGHTS.slice();
  const t = boxTier < 0 ? 0 : boxTier >= BOX_TIER_LIFT.length ? BOX_TIER_LIFT.length - 1 : boxTier | 0;
  // Never pull more than the sidegrade slot holds (keeps every weight ≥ 0).
  const lift = Math.min(BOX_TIER_LIFT[t] as number, w[0] as number);
  if (lift <= 0) return w;
  w[0] = (w[0] as number) - lift;
  // Distribute the lifted points into slots 1..5 by LIFT_SPLIT; the leftover (from flooring)
  // lands on the top slot so the row re-sums to exactly 100.
  let placed = 0;
  for (let i = 0; i < LIFT_SPLIT.length - 1; i++) {
    const add = Math.floor(lift * (LIFT_SPLIT[i] as number));
    w[i + 1] = (w[i + 1] as number) + add;
    placed += add;
  }
  w[PACING_SLOTS.length - 1] = (w[PACING_SLOTS.length - 1] as number) + (lift - placed);
  return w;
}

/**
 * Pick a pacing slot via a deterministic cumulative walk. `roll` is an integer in
 * `[0, sum(weights))` (the caller draws it once from its seeded RNG). Walks the weights in the
 * fixed PACING_SLOTS order and returns the chosen slot. Defensive: a non-positive total, or a roll
 * past the end, returns the LAST slot (the caller never gets undefined). Pure — no RNG, no mutation.
 */
export function rollUpgradeSlot(roll: number, weights: readonly number[] = REFERENCE_WEIGHTS): PacingSlot {
  let total = 0;
  for (let i = 0; i < weights.length; i++) total += weights[i] as number;
  if (total <= 0) return PACING_SLOTS[PACING_SLOTS.length - 1] as PacingSlot;
  let r = roll % total;
  if (r < 0) r += total;
  for (let i = 0; i < PACING_SLOTS.length && i < weights.length; i++) {
    r -= weights[i] as number;
    if (r < 0) return PACING_SLOTS[i] as PacingSlot;
  }
  return PACING_SLOTS[PACING_SLOTS.length - 1] as PacingSlot;
}

/** The total of a weight row (the modulus the caller should draw `roll` from). */
export function weightTotal(weights: readonly number[] = REFERENCE_WEIGHTS): number {
  let total = 0;
  for (let i = 0; i < weights.length; i++) total += weights[i] as number;
  return total;
}

/** Index of a tier in the canonical ascending order (for strict-ordering assertions). */
export function tierRank(tier: UpgradeTier): number {
  return UPGRADE_TIERS.indexOf(tier);
}
